import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download
} from 'lucide-react';
import { useApi } from '../../contexts/ApiContext';
import Sidebar from '../../components/Layout/Sidebar';
import Header from '../../components/Layout/Header';
import { Card, CardHeader, CardTitle } from '../../components/ui/Card';
import {
  CompactSeriesList,
  StatsOverview
} from '../../components/home';


function AnnouncementItem({ announcement }) {
  const typeConfig = {
    info: { icon: AlertCircle, color: 'bg-blue-50 text-blue-600' },
    success: { icon: CheckCircle2, color: 'bg-green-50 text-green-600' },
    warning: { icon: Clock, color: 'bg-amber-50 text-amber-600' },
    error: { icon: AlertCircle, color: 'bg-red-50 text-red-600' }
  };

  const config = typeConfig[announcement.type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div className="flex gap-3 p-2.5 rounded hover:bg-slate-50 transition-colors">
      <div className={`w-9 h-9 rounded flex items-center justify-center flex-shrink-0 ${config.color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-slate-800">{announcement.title}</h4>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{announcement.message}</p>
      </div>
    </div>
  );
}

export default function SeriesSelection() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { home } = useApi();

  const { data: dashboardData = { series: [], stats: {}, announcements: [] }, isLoading: loading } = useQuery({
    queryKey: ['home-dashboard'],
    queryFn: async () => {
      const result = await home.getDashboard();
      if (!result.success || !result.data) throw new Error(result.message || 'Failed to load dashboard');
      return {
        series: result.data.series || [],
        stats: result.data.stats || {},
        announcements: result.data.announcements || []
      };
    },
  });

  const handleSeriesSelect = (series) => {
    navigate(`/${series.slug}/season`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentSeries={null}
      />

      <Header
        sidebarCollapsed={sidebarCollapsed}
        onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        title="Series"
        subtitle="Choose a series to manage"
      />

      <main
        className={clsx(
          'pt-14 min-h-screen transition-all duration-200',
          sidebarCollapsed ? 'ml-14' : 'ml-56'
        )}
      >
        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
            <div className="space-y-5 order-2 lg:order-1">
              <StatsOverview stats={dashboardData.stats} loading={loading} />

              <Card padding="compact">
                <CardHeader border={false} className="mb-3 pb-0">
                  <div className="flex items-center justify-between">
                    <CardTitle>Eloy Relay Client</CardTitle>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      v1.5.1
                    </span>
                  </div>
                </CardHeader>
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Download the relay client for live timing integration.
                  </p>
                  <a
                    href="https://www.dropbox.com/scl/fi/osbe2o0tk8md1crdznw3b/Eloy_1.5.1_x64-setup.exe?rlkey=jpok0mr4evc23xwf5abjouosd&st=da8pzay6&dl=0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-wrg-blue-600 hover:bg-wrg-blue-700 text-white text-sm font-medium rounded transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download for Windows
                  </a>
                </div>
              </Card>

              {dashboardData.announcements.length > 0 && (
                <Card padding="compact">
                  <CardHeader border={false} className="mb-3 pb-0">
                    <CardTitle>Announcements</CardTitle>
                  </CardHeader>
                  <div className="space-y-1">
                    {dashboardData.announcements.slice(0, 3).map((announcement) => (
                      <AnnouncementItem key={announcement.id} announcement={announcement} />
                    ))}
                  </div>
                </Card>
              )}
            </div>

            <div className="lg:col-span-3 order-1 lg:order-2">
              <CompactSeriesList
                series={dashboardData.series}
                onSeriesSelect={handleSeriesSelect}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
